/**
 * The default, no-LLM parser: run the regex templates in `patterns.ts`, then
 * hand the structural match to the shared grounding/IR builder. Zero network
 * except the linker calls it delegates.
 */
import type {
  EntityLinker,
  RelationLinker,
} from "../linking/types.ts";
import type {
  ParseContext,
  ParseResult,
  SemanticParser,
} from "../parser/types.ts";
import { ParseError } from "../parser/types.ts";
import { analyze } from "./patterns.ts";
import { groundAndBuild } from "./ground.ts";

export interface DeterministicParserOptions {
  entityLinker: EntityLinker;
  relationLinker: RelationLinker;
  sourceId?: string;
}

export class DeterministicParser implements SemanticParser {
  readonly name = "deterministic";
  private readonly entityLinker: EntityLinker;
  private readonly relationLinker: RelationLinker;
  private readonly sourceId: string;

  constructor(opts: DeterministicParserOptions) {
    this.entityLinker = opts.entityLinker;
    this.relationLinker = opts.relationLinker;
    this.sourceId = opts.sourceId ?? "wikidata";
  }

  async parse(question: string, ctx?: ParseContext): Promise<ParseResult> {
    const match = analyze(question);
    if (!match) {
      throw new ParseError(
        "I couldn't match this question to a known pattern. Try forms like " +
          '"capital of France", "films directed by Christopher Nolan", or "tallest mountain".',
        {
          question,
          normalized: question.trim(),
          parser: this.name,
          template: "(none)",
          form: "select",
          entities: [],
          relations: [],
          notes: [],
        },
      );
    }
    return groundAndBuild(match, {
      entityLinker: this.entityLinker,
      relationLinker: this.relationLinker,
      sourceId: this.sourceId,
      parserName: this.name,
      question,
      ...(ctx?.signal ? { signal: ctx.signal } : {}),
      ...(ctx?.overrides ? { overrides: ctx.overrides } : {}),
    });
  }
}
